import React, { useEffect, useState } from 'react';

import { EnvironmentProvider } from '../../contexts/EnvironmentContext';
import { GA_TRACK_EVENTS_ACTIONS, GA_PARAMETER } from '../../utils/constants';
import { BANNER_POSITION, bannerPositions } from '../BannersApp/constants/banners';
import { FINANCING_HIGHLIGHT_KEY } from './utils/constants';
import SellerInfoSmall from './components/SellerInfo/SellerInfoSmall';
import SellerContactOptions from './components/SellerContactOptions/SellerContactOptions';
import { getHeroImageUrl } from '../../utils/getImageUrls';
import getSellerProfile from '../../repositories/getSellerProfile';
import { trackGAEvent } from '../../utils/gaTracking';
import createCustomDimension from '../../utils/createCustomDimension';
import ListingHeader from './components/ListingHeader/ListingHeader';
import Banners from '../BannersApp/components/Banners/Banners';
import OneStepBidding from '../BiddingApp/components/Bidding/OneStepBidding';

import { TListingClient } from 'types/TListing';
import { TSellerProfileClient } from 'types/TSellerProfile';

type TSellerSidebarAppProps = {
  appState: {
    listing: TListingClient;
  };
  environment: any;
  showBannersOnVip: boolean;
};

const SellerSidebarApp = ({ appState, environment, showBannersOnVip }: TSellerSidebarAppProps) => {
  const [profile, setProfile] = useState<TSellerProfileClient>({});
  const [isProfileLoaded, setIsProfileLoaded] = useState(false);
  const {
    listing: {
      itemId,
      title,
      gallery,
      priceInfo,
      seller,
      isCarAd,
      isAutomotiveAd,
      traits,
      highlights,
      flags: { isAdmarkt, hasCallTracking, allowTradeInRequest },
      bidsInfo: { isBiddingEnabled, bids, currentMinimumBid },
      isReserved,
    },
  } = appState;
  const [adBids, setAdBids] = useState<TListingClient['bidsInfo']['bids']>(bids);
  const showExtendedAsq = traits.includes('AUTOMOTIVE_EXTENDED_ASQ');
  const financeAvailableHighlight = highlights?.find(({ key }) => key === FINANCING_HIGHLIGHT_KEY);

  useEffect(() => {
    (async () => {
      setProfile(
        await getSellerProfile({
          sellerId: seller.id,
          requestExternalReviews: seller.requestExternalReviews,
          showExtendedAsq,
        }),
      );
      setIsProfileLoaded(true);
    })();
  }, [seller.id, seller.requestExternalReviews, showExtendedAsq]);

  const hasTrackedSellerInfo = React.useRef(false);

  useEffect(() => {
    if (isProfileLoaded && !hasTrackedSellerInfo.current) {
      const { bankAccount, identification, paymentMethod } = profile;
      const { averageScore, numberOfReviews } = profile.reviews?.[0] ?? {};
      const availableReviewSystems = profile.reviews?.map((review) => review.reviewSystem);

      trackGAEvent(GA_TRACK_EVENTS_ACTIONS.SELLER_INFO_AVAILABLE, '', {
        ...createCustomDimension({
          dimension: GA_PARAMETER.SELLER_INFO,
          payload: JSON.stringify({
            YearsActive: seller.activeYears,
            ...(bankAccount && { BankAccount: bankAccount }),
            ...(identification && { IDChecked: identification }),
            ...(paymentMethod && { IdealPayment: paymentMethod?.name === 'ideal' }),
            StarRating: averageScore,
            NumberReviews: numberOfReviews,
            AvailableReviewSystems: availableReviewSystems,
          }),
        }),
      });

      hasTrackedSellerInfo.current = true;
    }
  }, [isProfileLoaded, profile, seller.activeYears]);

  return (
    <EnvironmentProvider value={environment}>
      <ListingHeader listing={appState.listing} financeAvailableHighlight={financeAvailableHighlight?.key} />
      <SellerInfoSmall seller={seller} isAutomotiveAd={isAutomotiveAd} profile={profile} isAdmarkt={isAdmarkt} />
      <SellerContactOptions
        itemId={itemId}
        title={title}
        adImageThumbnail={getHeroImageUrl(gallery.imageUrls[0])}
        priceInfo={priceInfo}
        seller={seller}
        isAutomotiveAd={isAutomotiveAd}
        allowTradeInRequest={allowTradeInRequest}
        isAdmarkt={isAdmarkt}
        hasCallTracking={hasCallTracking}
        traits={traits}
        profile={profile}
        isCarAd={isCarAd}
        isBiddingEnabled={isBiddingEnabled}
        adBids={adBids}
        onUpdateBidsHandler={setAdBids}
        currentMinimumBid={currentMinimumBid}
        isReserved={isReserved}
      />
      {isBiddingEnabled && (
        <div id="bidding-root" className="display-block-m">
          <OneStepBidding
            priceType={priceInfo.priceType}
            itemId={itemId}
            adBids={adBids}
            onUpdateBidsHandler={setAdBids}
            title={title}
            currentMinimumBid={currentMinimumBid}
            sellerType={seller.sellerType}
            withPhoneNumber={isCarAd}
            type="INFO"
            isReserved={isReserved}
          />
        </div>
      )}
      {showBannersOnVip && (
        <div
          id={bannerPositions[BANNER_POSITION.STATIC.RIGHT].containerId}
          className="display-block-m right-banner-root-container"
        >
          <Banners position={BANNER_POSITION.STATIC.RIGHT} />
        </div>
      )}
    </EnvironmentProvider>
  );
};

export default SellerSidebarApp;
